/**
 * What this project knows, on the project's own screen.
 *
 * Phase 1 put the knowledge bases here and proved the place was the missing
 * part, not the model. Phase 2 makes it about documents rather than bases,
 * because a base is filing-cabinet vocabulary and a document is the thing a
 * person actually has.
 *
 * Two acts, never confused, on every document. Taking a document out of a
 * project leaves the file alone; deleting it does not. The first version of
 * this only offered the first act for borrowed documents, so a document that
 * lived here could be deleted and not removed, and Frank's glass test failed
 * on exactly that. Now both sit in one menu, the destructive one asks first
 * and says where the file disappears from.
 *
 * Everything is a real tap target and nothing is revealed by hovering. That
 * was the lesson of the phase 1 glass test.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { FileText, Plus, Upload, Trash2, Loader2, CheckCircle, XCircle, X, FolderInput, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/** Same list the knowledge screen accepts, so a file is not refused in one place and taken in the other. */
const ACCEPT = ".txt,.md,.pdf,.docx,.xlsx,.pptx,.py,.js,.ts,.jsx,.tsx,.css,.html,.json,.csv,.rs,.go,.java,.cpp,.c,.rb,.sql,.sh,.yaml,.yml,.toml,.ini,.cfg";

/** The base made on the first upload, so the button works before anything is filed. */
const DEFAULT_BASE_NAME = "Documents";

/**
 * Pull a sentence out of an error the user can read.
 *
 * A refused plan comes back as 402 with `detail` as an object, and handing
 * that to a toast prints [object Object]. Anything else is a plain string.
 */
export function refusalText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  return fallback;
}

export default function ProjectKnowledge({ projectId, onOpenDocument }) {
  const { t } = useTaal();
  const [documents, setDocuments] = useState([]);
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [borrowOpen, setBorrowOpen] = useState(false);
  const [borrowable, setBorrowable] = useState(null);
  const [menuFor, setMenuFor] = useState(null);      // document id whose menu is open
  const [confirmDelete, setConfirmDelete] = useState(null);
  // De globale banken van deze gebruiker, met of ze HIER meewegen. BLOK 8
  // fase 4. De volledige lijst komt van de server en niet alleen de
  // uitzonderingen: de rest zelf afleiden is precies waar een tweede waarheid
  // ontstaat.
  const [globalBases, setGlobalBases] = useState([]);
  const fileInput = useRef(null);

  const loadDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/documents`);
      // A list, or nothing. Trusting the shape here once cost the whole
      // project screen: anything that is not an array reached .map and took
      // the page down to a blank, which reads as "the app is broken" rather
      // than "this panel has no data".
      setDocuments(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDocuments([]);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadDocuments();
      try {
        const res = await axios.get(`${API}/knowledge/bases?project_id=${projectId}`);
        if (!cancelled) setBases(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setBases([]);
      }
      try {
        const res = await axios.get(`${API}/knowledge/bases/in-project/${projectId}`);
        if (!cancelled) setGlobalBases(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Een eigen catch en niet dezelfde als hierboven, want dit is een
        // ANDERE vraag. Viel dit onder een gedeelde catch, dan zou een fout
        // hier de projectbanken meenemen, en dan lijkt een project ineens
        // leeg terwijl er alleen een schakelaarlijst ontbreekt.
        if (!cancelled) setGlobalBases([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, loadDocuments]);

  /**
   * Zet een globale bank hier aan of uit. BLOK 8 fase 4.
   *
   * DE VECTOREN WORDEN NIET GERAAKT. De bank blijft, de bestanden blijven, en
   * in elk ander project weegt zij gewoon mee. Alleen de weging HIER valt weg.
   * Dat is dezelfde grens die deze component al trekt tussen "uit het project
   * halen" en "weggooien".
   *
   * Het scherm loopt niet vooruit op de server. Een schakelaar die meteen
   * omgaat en daarna stil terugvalt bij een fout, laat je denken dat er iets
   * uitstaat wat gewoon meeweegt, en dat is precies de soort onwaarheid waar
   * de vluchtrecorder voor bestaat.
   */
  const setGlobalBaseCounts = async (kb, counts) => {
    try {
      await axios.put(`${API}/knowledge/bases/${kb.id}/in-project/${projectId}`, { counts });
      setGlobalBases((prev) =>
        prev.map((b) => (b.id === kb.id ? { ...b, counts_here: counts } : b)));
      toast.success(counts
        ? `${kb.name} counts in this project again`
        : `${kb.name} no longer counts here. The files are untouched.`);
    } catch (e) {
      toast.error(refusalText(e, "Could not change that"));
    }
  };

  /** The base new uploads go into, made on the spot the first time. */
  const targetBase = async () => {
    if (bases.length) return bases[0].id;
    const res = await axios.post(`${API}/knowledge/bases`, {
      project_id: projectId, name: DEFAULT_BASE_NAME, description: "",
    });
    setBases((prev) => [...prev, res.data]);
    return res.data.id;
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const kbId = await targetBase();
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const res = await axios.post(`${API}/knowledge/bases/${kbId}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const added = res.data.map((d) => ({ ...d, via: "knowledge_base" }));
      setDocuments((prev) => [...added, ...prev]);
      toast.success(t("{n} document(en) aan dit project toegevoegd", { n: files.length }));
    } catch (error) {
      toast.error(refusalText(error, "Upload failed"));
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  const openBorrow = async () => {
    setBorrowOpen(true);
    if (borrowable !== null) return;
    try {
      const res = await axios.get(`${API}/knowledge/documents/available?exclude_project_id=${projectId}`);
      setBorrowable(res.data);
    } catch {
      setBorrowable([]);
    }
  };

  const borrow = async (doc) => {
    try {
      await axios.post(`${API}/projects/${projectId}/documents/${doc.id}`);
      setDocuments((prev) => [{ ...doc, via: "linked" }, ...prev]);
      setBorrowOpen(false);
      toast.success(t("{f} telt nu mee in dit project", { f: doc.filename }));
    } catch (error) {
      toast.error(refusalText(error, "Could not add that document"));
    }
  };

  /** Out of this project. The file is not touched, wherever it lives. */
  const removeFromProject = async (doc) => {
    setMenuFor(null);
    try {
      await axios.delete(`${API}/projects/${projectId}/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      setBorrowable(null); // it can be added back, so the picker is stale
      toast.success(`${doc.filename} is out of this project. It is still in ${doc.knowledge_base_name || "its knowledge base"}.`);
    } catch (error) {
      toast.error(refusalText(error, "Could not take that document out"));
    }
  };

  /** Gone, everywhere. Only ever reached through the confirmation. */
  const deleteEverywhere = async (doc) => {
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/knowledge/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      setBorrowable(null);
      toast.success(t("{f} verwijderd", { f: doc.filename }));
    } catch (error) {
      toast.error(refusalText(error, "Could not delete that document"));
    }
  };

  return (
    <div data-testid="project-knowledge">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-medium text-muted-foreground uppercase">{t("Kennis")}</h2>
        <span className="text-xs text-muted-foreground" data-testid="project-doc-count">{documents.length}</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("Laden")}
        </div>
      ) : documents.length === 0 ? (
        <p className="text-sm text-muted-foreground mb-3" data-testid="project-docs-empty">
          {t("Nog geen documenten. Alles hier wordt vanzelf doorzocht als je in dit project chat.")}
        </p>
      ) : (
        <div className="space-y-1 mb-3" data-testid="project-doc-list">
          {documents.map((doc) => (
            <div key={doc.id} className="relative flex items-start gap-2 px-1 py-1.5 rounded">
              <FileText className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground mt-1" />
              {/* min-w-0 twice on purpose. A flex child will not shrink below
                  its content by default, so without it a long filename pushes
                  everything to its right off the edge instead of truncating,
                  and the menu goes with it. */}
              <button
                type="button"
                onClick={() => onOpenDocument?.(doc)}
                data-testid={`project-doc-open-${doc.id}`}
                className="min-w-0 flex-1 text-left"
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="truncate text-sm">{doc.filename}</span>
                  {doc.status === "ready" ? (
                    <CheckCircle className="h-3 w-3 flex-shrink-0 text-ok" />
                  ) : doc.status === "error" ? (
                    <XCircle className="h-3 w-3 flex-shrink-0 text-destructive" />
                  ) : (
                    <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
                  )}
                </span>
                {/* Where it came from, on every document. Half the list used
                    to be labelled and half not, so "remove" looked like the
                    same act on things it treats differently. */}
                <span className="block text-xs text-muted-foreground truncate" data-testid={`project-doc-origin-${doc.id}`}>
                  {doc.via === "linked"
                    ? `borrowed from ${doc.knowledge_base_name || "another project"}`
                    : "uploaded here"}
                </span>
                {/* Why it failed, in words, and not only as a red cross. The
                    cross was all there was, and on this screen the reason was
                    not shown at all: a document you cannot search, with
                    nothing saying what to do about it. Not truncated, because
                    the sentence is the point. */}
                {doc.status === "error" && doc.error ? (
                  <span className="block text-xs text-destructive" data-testid={`project-doc-error-${doc.id}`}>
                    {doc.error}
                  </span>
                ) : null}
              </button>

              {/* One menu, two acts, and they are never one tap apart from
                  each other by accident: the destructive one asks first.
                  ml-auto pins it to the right edge whatever the name does. */}
              <button
                type="button"
                onClick={() => setMenuFor(menuFor === doc.id ? null : doc.id)}
                data-testid={`project-doc-menu-${doc.id}`}
                aria-label={`Options for ${doc.filename}`}
                className="tap-target ml-auto flex items-center justify-center h-8 w-8 flex-shrink-0 text-muted-foreground"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>

              {menuFor === doc.id && (
                <div className="absolute right-0 top-9 z-30 w-60 max-w-[80vw] rounded-xl border bg-popover shadow-lg overflow-hidden">
                  <button
                    type="button"
                    data-testid={`project-doc-unlink-${doc.id}`}
                    onClick={() => removeFromProject(doc)}
                    className="w-full flex items-start gap-2 px-3 py-3 text-sm text-left hover:bg-muted"
                  >
                    <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="block">{t("Uit het project halen")}</span>
                      <span className="block text-xs text-muted-foreground">{t("Blijft in de kennisbank")}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid={`project-doc-delete-${doc.id}`}
                    onClick={() => { setMenuFor(null); setConfirmDelete(doc); }}
                    className="w-full flex items-start gap-2 px-3 py-3 text-sm text-left border-t text-destructive hover:bg-muted"
                  >
                    <Trash2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="block">{t("Verwijderen")}</span>
                      <span className="block text-xs text-muted-foreground">{t("Verwijdert het bestand zelf")}</span>
                    </span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <input
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        data-testid="project-doc-input"
        ref={fileInput}
        onChange={handleUpload}
      />
      <div className="space-y-2">
        <Button
          variant="outline"
          className="w-full justify-center gap-2 h-10 md:h-9"
          data-testid="project-doc-upload"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span>{t("Een document toevoegen")}</span>
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-center gap-2 h-10 md:h-9 text-muted-foreground"
          data-testid="project-doc-borrow"
          onClick={openBorrow}
        >
          <FolderInput className="h-4 w-4" />
          <span>{t("Gebruik er een uit een ander project")}</span>
        </Button>
      </div>

      {/* BLOK 8 fase 4. Alleen zichtbaar als er globale banken ZIJN; een lege
          kop met een uitleg eronder over iets wat je niet hebt, is ruis.
          En de zin eronder staat er omdat "uit" hier makkelijk gelezen wordt
          als "weg": dit raakt de bestanden niet en geldt alleen hier. */}
      {globalBases.length > 0 && (
        <div className="mt-4 pt-3 border-t" data-testid="global-bases-here">
          <h3 className="text-sm font-medium text-muted-foreground uppercase mb-1">
            {t("Je eigen kennis")}
          </h3>
          <p className="text-xs text-muted-foreground mb-2">
            {t("Telt mee in elke chat tenzij je het hier uitzet. Uitzetten verandert niets aan de bestanden, en niets in je andere projecten.")}
          </p>
          <div className="space-y-1">
            {globalBases.map((kb) => (
              <label
                key={kb.id}
                className="flex items-center gap-2 px-1 py-1.5 rounded cursor-pointer"
                data-testid={`global-base-${kb.id}`}
              >
                <input
                  type="checkbox"
                  checked={kb.counts_here !== false}
                  onChange={(e) => setGlobalBaseCounts(kb, e.target.checked)}
                  data-testid={`global-base-toggle-${kb.id}`}
                  aria-label={`Count ${kb.name} in this project`}
                  className="h-4 w-4 flex-shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{kb.name}</span>
                  {/* Wat erin zit, zodat "1285 notities uitzetten" niet
                      hetzelfde voelt als een vinkje bij iets onbekends. */}
                  <span className="block text-xs text-muted-foreground truncate"
                        data-testid={`global-base-detail-${kb.id}`}>
                    {kb.document_count === 1 ? t("1 document") : t("{n} documenten", { n: kb.document_count })}
                    {kb.source_folder ? ` ${t("uit {map}", { map: kb.source_folder })}` : ""}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <Dialog open={Boolean(confirmDelete)} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("{naam} verwijderen?", { naam: confirmDelete?.filename })}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground" data-testid="project-doc-delete-warning">
            {t("Dit verwijdert het bestand zelf. Het verdwijnt uit elk project dat het gebruikt en uit {kennisbank}, en dat is niet terug te draaien. Wil je het alleen uit dit project halen, gebruik dan Uit het project halen.",
              { kennisbank: confirmDelete?.knowledge_base_name || t("de kennisbank") })}
          </p>
          <DialogFooter>
            <Button variant="outline" data-testid="project-doc-delete-cancel" onClick={() => setConfirmDelete(null)}>{t("Annuleren")}</Button>
            <Button variant="destructive" data-testid="project-doc-delete-confirm" onClick={() => deleteEverywhere(confirmDelete)}>
              {t("Overal verwijderen")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={borrowOpen} onOpenChange={setBorrowOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Een document uit een ander project gebruiken")}</DialogTitle></DialogHeader>
          {borrowable === null ? (
            <div className="py-6 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : borrowable.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="project-borrow-empty">
              {t("Je andere projecten hebben nog geen documenten.")}
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto -mx-2">
              {borrowable.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  data-testid={`project-borrow-${doc.id}`}
                  onClick={() => borrow(doc)}
                  className="w-full flex items-center gap-2 px-3 py-3 text-left hover:bg-muted rounded"
                >
                  <Plus className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm">{doc.filename}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {doc.taken_out_here
                        ? "taken out of this project, put it back"
                        : `${doc.project_name}${doc.knowledge_base_name ? ` · ${doc.knowledge_base_name}` : ""}`}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
